import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Save, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/utils2";

export default function CodesAdmin() {
  const [codes, setCodes] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [showCodes, setShowCodes] = useState(false);
  const [drafts, setDrafts] = useState({}); // dept -> new code
  const [savingDept, setSavingDept] = useState(null);

  const load = async () => {
    const [{ data: c }, { data: a }] = await Promise.all([
      api.get("/department-codes"),
      api.get("/department-codes/failed-attempts"),
    ]);
    setCodes(c); setAttempts(a);
  };
  useEffect(() => { load(); }, []);

  const save = async (dept) => {
    const next = (drafts[dept] || "").trim().toUpperCase();
    if (!next) { toast.error("Code cannot be empty"); return; }
    setSavingDept(dept);
    try {
      const { data } = await api.patch(`/department-codes/${encodeURIComponent(dept)}`, { code: next });
      setCodes((cs) => cs.map((c) => c.department === dept ? data : c));
      setDrafts((d) => { const n = { ...d }; delete n[dept]; return n; });
      toast.success(`${dept} code updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingDept(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary"/> Department codes
          </h3>
          <p className="text-xs text-muted-foreground">Contractors must enter the correct code before they can submit an absence notification.</p>
        </div>
        <Button data-testid="codes-toggle-visibility" size="sm" variant="outline" onClick={() => setShowCodes((v) => !v)} className="gap-1.5">
          {showCodes ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
          {showCodes ? "Hide codes" : "Reveal codes"}
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table data-testid="codes-table">
          <TableHeader><TableRow>
            <TableHead>Department</TableHead>
            <TableHead>Current code</TableHead>
            <TableHead>New code</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {codes.map((c) => (
              <TableRow key={c.department} data-testid={`code-row-${c.department}`}>
                <TableCell className="font-medium">{c.department}</TableCell>
                <TableCell className="font-mono">
                  {showCodes ? c.code : "•".repeat(Math.max(c.code.length, 6))}
                </TableCell>
                <TableCell>
                  <Input
                    data-testid={`code-input-${c.department}`}
                    value={drafts[c.department] ?? ""}
                    onChange={(e) => setDrafts({ ...drafts, [c.department]: e.target.value.toUpperCase() })}
                    placeholder="Enter new code…"
                    className="font-mono max-w-[200px]"
                  />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(c.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`code-save-${c.department}`}
                    size="sm"
                    disabled={!drafts[c.department] || savingDept === c.department}
                    onClick={() => save(c.department)}
                    className="gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5"/> Save
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div>
        <h4 className="font-heading text-base font-semibold flex items-center gap-2 mb-2">
          <ShieldAlert className="w-4 h-4 text-amber-600"/> Recent failed attempts
        </h4>
        {attempts.length === 0 ? (
          <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-6 text-center">
            No failed attempts logged.
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E] max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-[#1E1E1E] z-10">
                <TableRow><TableHead>Attempted code</TableHead><TableHead>When</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {attempts.map((a) => (
                  <TableRow key={a.id} data-testid={`attempt-${a.id}`}>
                    <TableCell className="font-mono text-muted-foreground">{showCodes ? a.attempted_code : "•".repeat(Math.max(a.attempted_code.length, 4))}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDateTime(a.at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
